<template>
  <page-wrapper ref="pageEl" title="รายงาน MNC Wallet (download)">
    <div class="page-container">
      <LoadingOverlay :active="isLoading" />

      <!-- NOTE: Transfer Home Section -->
      <section class="report-section">
        <h3 class="report-section__title">รายงาน Transfer home</h3>

        <div class="report-section__form">
          <div class="field">
            <div class="label">เลือกปี</div>
            <el-select
              v-model="selectedYear"
              :disabled="isLoading"
              placeholder="เลือกปี"
              style="width: 100%"
              filterable
              clearable
            >
              <el-option
                v-for="year in yearOptions"
                :key="year.value"
                :label="year.label"
                :value="year.value"
              ></el-option>
            </el-select>
            <div v-if="!selectedYear || targetCoin" class="hint">
              เลือกย้อนหลังได้ไม่เกิน 2 ปี
            </div>
            <div v-else-if="!isCoinsLoaded" class="hint hint--warning">
              โหลดรายการ Coin ไม่สำเร็จ กรุณารีเฟรชหน้าอีกครั้ง
            </div>
            <div v-else class="hint hint--warning">
              ไม่พบข้อมูล Coin ของปีที่เลือก
            </div>
          </div>

          <div class="field field--action">
            <el-button
              :disabled="isTransferHomeDownloadDisabled"
              type="primary"
              icon="el-icon-download"
              @click.prevent="downloadTransferHomeReport"
            >
              ดาวน์โหลด
            </el-button>
          </div>
        </div>
      </section>

      <el-divider></el-divider>

      <section class="report-section">
        <h3 class="report-section__title">รายงาน Balance</h3>

        <div class="report-section__form">
          <div class="field">
            <div class="label">เลือกวันที่</div>
            <el-date-picker
              v-model="selectedBalanceDate"
              :picker-options="balancePickerOptions"
              :disabled="isLoading"
              type="date"
              format="dd MMMM yyyy"
              value-format="yyyy-MM-dd"
              placeholder="เลือกวันที่"
              style="width: 100%"
              clearable
            ></el-date-picker>
            <div class="hint">
              เลือกย้อนหลังได้ไม่เกิน {{ balanceMaxDaysBack }} วัน
              <br />
              ระบบจะดึงข้อมูลตอนเที่ยงคืน (00:00) ของวันที่เลือก
            </div>
          </div>

          <div class="field field--action">
            <el-button
              :disabled="!selectedBalanceDate || isLoading"
              type="primary"
              icon="el-icon-download"
              @click.prevent="downloadBalanceReport"
            >
              ดาวน์โหลด
            </el-button>
          </div>
        </div>
      </section>
    </div>
  </page-wrapper>
</template>

<script>
// Dependencies
import { DateTime } from "luxon"
import { backendAdminClient } from "@/dependencies"
import { toLocaleTH } from "@/lib/dateTime"

// Components
import LoadingOverlay from "@/lib/components/LoadingOverlay"
import PageWrapper from "@/lib/components/PageWrapper"

const YEAR_OPTION_COUNT = 2
const BALANCE_MAX_DAYS_BACK = 120
const COIN_NAME_PREFIX = "MN0"

// a coin is bound to a year by the last 2 digits of the Gregorian year
function toCoinName(year) {
  if (!year) return null

  return `${COIN_NAME_PREFIX}${String(year).slice(-2)}`
}

export default {
  components: {
    LoadingOverlay,
    PageWrapper,
  },
  data() {
    return {
      selectedYear: undefined,
      coins: [],
      isCoinsLoaded: false,
      selectedBalanceDate: undefined,
      balanceMaxDaysBack: BALANCE_MAX_DAYS_BACK,
      isLoading: false,
    }
  },
  computed: {
    targetCoin() {
      const coinName = toCoinName(this.selectedYear)

      if (!coinName) return null

      return this.coins.find((coin) => coin.coin_name === coinName) || null
    },
    isTransferHomeDownloadDisabled() {
      return !this.targetCoin || this.isLoading
    },
    yearOptions() {
      const currentYear = DateTime.now().setZone("Asia/Bangkok").startOf("year")

      return Array.from({ length: YEAR_OPTION_COUNT }, (_, index) => {
        const year = currentYear.minus({ years: index })

        return {
          value: year.toFormat("yyyy"),
          label: toLocaleTH(year).toFormat("yyyy"),
        }
      })
    },
    snapshotDate() {
      if (!this.selectedBalanceDate) return null

      // the snapshot runs at 00:00 of the picked date, so its data covers
      // up to the end of the previous day — that is the date the API wants
      return DateTime.fromISO(this.selectedBalanceDate)
        .minus({ days: 1 })
        .toFormat("yyyy-MM-dd")
    },
    balancePickerOptions() {
      const today = DateTime.local().startOf("day")

      const earliest = today.minus({ days: BALANCE_MAX_DAYS_BACK - 1 })

      return {
        disabledDate(date) {
          const target = DateTime.fromJSDate(date).startOf("day")

          return target < earliest || target > today
        },
      }
    },
  },
  created() {
    this.fetchCoins()
  },
  methods: {
    async fetchCoins() {
      this.isLoading = true

      try {
        const { data } = await backendAdminClient.morningCoin.getCoins()

        this.coins = data.result
        this.isCoinsLoaded = true
      } catch (error) {
        const message = error.response
          ? error.response.data.message
          : error.message

        this.$message.error(
          `ไม่สามารถโหลดรายการ Morning Coin ได้เนื่องจาก "${message || "ไม่พบข้อมูล"}"`,
        )
      } finally {
        this.isLoading = false
      }
    },
    async downloadTransferHomeReport() {
      if (this.isTransferHomeDownloadDisabled) return

      const year = DateTime.fromFormat(this.selectedYear, "yyyy", {
        zone: "Asia/Bangkok",
      })

      await this.downloadReport({
        request: () =>
          backendAdminClient.report.downloadTransferHomeMncStatusReport({
            start_at: year.startOf("year").toFormat("yyyy-MM-dd"),
            end_at: year.endOf("year").toFormat("yyyy-MM-dd"),
            coin_id: [this.targetCoin.coin_id],
          }),
        fileName: `${this.downloadTimestamp()}-mnc-transfer-home-status-reports.xlsx`,
      })
    },
    async downloadBalanceReport() {
      if (!this.selectedBalanceDate) return

      await this.downloadReport({
        request: () =>
          backendAdminClient.report.downloadBalanceSnapshotReport({
            snapshot_date: this.snapshotDate,
          }),
        fileName: `${this.downloadTimestamp()}-mnc-balance-reports.xlsx`,
      })
    },
    async downloadReport({ request, fileName }) {
      try {
        await this.$confirm(
          "การดาวน์โหลดจะใช้เวลาดำเนินการสักครู่ กรุณาอย่าออกจากหน้าจอ หรือรีเฟรชหน้า",
          "แจ้งเตือน",
          {
            confirmButtonText: "ดาวน์โหลด",
            cancelButtonText: "ปิด",
            type: "info",
          },
        )
      } catch (error) {
        return
      }

      this.isLoading = true

      try {
        const { data } = await request()

        if (!data) {
          this.$message.error("ไม่พบข้อมูลที่ต้องการดาวน์โหลด")
          return
        }

        this.saveFile(data, fileName)
      } catch (error) {
        const message = error.response
          ? error.response.data.message
          : error.message

        this.$message.error(
          `ไม่สามารถดาวน์โหลดรายงานได้เนื่องจาก "${message || "ไม่พบข้อมูล"}"`,
        )
      } finally {
        this.isLoading = false
      }
    },
    saveFile(data, fileName) {
      const blob = new Blob([data])
      const link = document.createElement("a")

      link.href = window.URL.createObjectURL(blob)
      link.download = fileName
      link.click()

      window.URL.revokeObjectURL(link.href)
      link.remove()
    },
    downloadTimestamp() {
      return DateTime.utc()
        .setZone("Asia/Bangkok")
        .toFormat("yyyy-MM-dd-HHmmss")
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.page-container {
  padding: 1rem;
  margin: 0;
}

.report-section__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.report-section__form {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
}

.field {
  flex: 1 1 260px;
  max-width: 420px;
}

.field--action {
  flex: 0 0 auto;
  max-width: none;
  padding-top: 1.4rem;
}

.label {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.hint {
  font-size: 0.8rem;
  color: #909399;
  margin-top: 0.25rem;
}

.hint--warning {
  color: #e6a23c;
}

@media screen and (max-width: 768px) {
  .field,
  .field--action {
    flex-basis: 100%;
    max-width: none;
  }

  .field--action {
    padding-top: 0;
  }
}
</style>
